import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { completeTwoFactor, connect, type ConnectResult } from "../api/account";
import { useAuth } from "../auth/useAuth";
import { apiErrorMessage } from "../api/errorMessage";
import { createOAuthPkce, rememberStateFromRedirect } from "../auth/tokens";
import { validateEmail, validatePassword } from "../auth/validation";

type Step = "email" | "password" | "two_factor";

export default function Login() {
  const { markAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const storedChallenge =
    searchParams.get("two_factor") === "1"
      ? (sessionStorage.getItem("meridian_totp_challenge") ?? "")
      : "";
  const [step, setStep] = useState<Step>(
    storedChallenge ? "two_factor" : "email",
  );
  const [action, setAction] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [challenge, setChallenge] = useState(storedChallenge);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  // After a successful password reset we bounce the user here with
  // `?reset=success` so they see confirmation that their new password
  // took effect.
  const resetSuccess = searchParams.get("reset") === "success";
  const passwordChanged = searchParams.get("password") === "changed";

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    const emailCheck = validateEmail(email);
    if (!emailCheck.ok) {
      setError(emailCheck.message);
      return;
    }

    setLoading(true);
    try {
      const result = await connect({ email: email.trim() });
      await handleResult(result);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    const emailCheck = validateEmail(email);
    if (!emailCheck.ok) {
      setError(emailCheck.message);
      return;
    }
    const passwordCheck = validatePassword(password, action);
    if (!passwordCheck.ok) {
      setError(passwordCheck.message);
      return;
    }

    setLoading(true);
    try {
      const result = await connect({ email: email.trim(), password });
      await handleResult(result);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResult(result: ConnectResult) {
    switch (result.status) {
      case "authenticated":
        await markAuthenticated();
        break;
      case "password_required":
        setAction(result.action);
        setStep("password");
        break;
      case "oauth_redirect":
        // The server embeds the OAuth state in the redirect URL we're
        // about to follow. Pull it out so we can verify it matches on
        // callback — guards against an attacker feeding us a crafted
        // callback URL.
        rememberStateFromRedirect(result.url);
        setRedirecting(true);
        window.location.href = result.url;
        break;
      case "two_factor_required":
        setChallenge(result.challenge);
        setStep("two_factor");
        setPassword("");
        break;
    }
  }

  async function handleTwoFactorSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!twoFactorCode.trim()) {
      setError("Enter your six-digit code or a two-step backup code.");
      return;
    }
    setLoading(true);
    try {
      await completeTwoFactor(challenge, twoFactorCode.trim());
      sessionStorage.removeItem("meridian_totp_challenge");
      await markAuthenticated();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not verify that code."));
    } finally {
      setLoading(false);
    }
  }

  async function handleOAuth(source: string) {
    setError("");
    setLoading(true);

    try {
      const { challenge } = await createOAuthPkce("auth");
      const result = await connect({
        source,
        email: email.trim() || undefined,
        code_challenge: challenge,
      });
      if (result.status === "oauth_redirect") {
        rememberStateFromRedirect(result.url);
        setRedirecting(true);
        window.location.href = result.url;
      } else {
        await handleResult(result);
      }
    } catch (err) {
      setError(apiErrorMessage(err));
      setLoading(false);
    }
  }

  const anyLoading = loading || redirecting;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Meridian</h1>
          <p>Sign in to your account</p>
        </div>

        {(resetSuccess || passwordChanged) && !error && (
          <div className="info-message">
            Your password has been updated. Older password sessions can no
            longer refresh. Sign in with your new password.
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {redirecting && (
          <div className="info-message">
            Redirecting to your identity provider…
          </div>
        )}

        {step === "email" && (
          <>
            <form onSubmit={handleEmailSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  autoComplete="email"
                  maxLength={254}
                  disabled={anyLoading}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={anyLoading}
              >
                {loading ? "Checking…" : "Continue"}
              </button>
            </form>

            <div className="divider">
              <span>or continue with</span>
            </div>

            <div className="oauth-buttons">
              <button
                type="button"
                className="btn btn-oauth"
                onClick={() => handleOAuth("google")}
                disabled={anyLoading}
                aria-busy={anyLoading}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {redirecting ? "Redirecting…" : "Google"}
              </button>
              <button
                type="button"
                className="btn btn-oauth"
                onClick={() => handleOAuth("microsoft")}
                disabled={anyLoading}
                aria-busy={anyLoading}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path fill="#F25022" d="M1 1h10v10H1z" />
                  <path fill="#7FBA00" d="M13 1h10v10H13z" />
                  <path fill="#00A4EF" d="M1 13h10v10H1z" />
                  <path fill="#FFB900" d="M13 13h10v10H13z" />
                </svg>
                {redirecting ? "Redirecting…" : "Microsoft"}
              </button>
            </div>
          </>
        )}

        {step === "password" && (
          <form onSubmit={handlePasswordSubmit} noValidate>
            <div className="form-group">
              <label>Email</label>
              <div className="email-display">
                {email}
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                    setError("");
                  }}
                  disabled={anyLoading}
                >
                  Change
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="password">
                {action === "register" ? "Create a password" : "Password"}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={
                  action === "register"
                    ? "At least 12 characters"
                    : "Enter your password"
                }
                required
                autoFocus
                autoComplete={
                  action === "register" ? "new-password" : "current-password"
                }
                minLength={action === "register" ? 12 : undefined}
                disabled={anyLoading}
              />
              {action === "register" && (
                <p className="hint">At least 12 characters.</p>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={anyLoading}
            >
              {loading
                ? "Please wait…"
                : action === "register"
                  ? "Create Account"
                  : "Sign In"}
            </button>

            {action === "login" && (
              <div className="auth-link-row">
                <Link to="/forgot-password" className="btn-link">
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>
        )}
        {step === "two_factor" && (
          <form onSubmit={handleTwoFactorSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="two-factor-code">
                Authenticator or two-step backup code
              </label>
              <input
                id="two-factor-code"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                disabled={anyLoading}
                aria-describedby="two-factor-help"
              />
              <p id="two-factor-help" className="hint">
                Enter the current code from your authenticator, or one unused
                two-step backup code. Backup codes cannot unlock encrypted data.
              </p>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={anyLoading}
            >
              {loading ? "Verifying…" : "Verify and sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
